/**
 * Wrap crowded Portfolio x-axis studio names onto lines (same idea as
 * intelligence MarketGenreBarChart / SystemRatios region bars).
 * Call after `xAxisHighLight`. Tick labels wrap + truncate; hover uses the same
 * visual-width wrap but keeps the full name (no "...").
 *
 * Budget is visual width units: Latin ≈ 1, CJK/fullwidth ≈ 2, so Chinese names
 * do not consume a full Latin-length line and blow past the axis band.
 */

/** Approximate on-screen width vs a typical Latin chart axis glyph. */
const getCharVisualWidth = (ch: string): number => {
  const code = ch.codePointAt(0) ?? 0;
  // Wide East-Asian glyphs ≈ 2× Latin width in chart fonts (not string.length).
  if (
    (code >= 0x1100 && code <= 0x115f) // Hangul Jamo (Korean)
    || (code >= 0x2e80 && code <= 0xa4cf) // CJK radicals → ideographs, kana, etc. (Chinese)
    || (code >= 0xac00 && code <= 0xd7a3) // Hangul syllables (Korean)
    || (code >= 0xf900 && code <= 0xfaff) // CJK compatibility ideographs 
    || (code >= 0xfe10 && code <= 0xfe19) // Vertical forms
    || (code >= 0xfe30 && code <= 0xfe6f) // CJK compatibility forms 
    || (code >= 0xff00 && code <= 0xff60) // Fullwidth ASCII (Ａ, １, …)
    || (code >= 0xffe0 && code <= 0xffe6) // Fullwidth symbols
    || (code >= 0x20000 && code <= 0x2fa1f) // CJK Extension B+
  ) {
    return 2;
  }
  return 1;
};

const getVisualWidth = (text: string): number => {
  let width = 0;
  for (const ch of text) {
    width += getCharVisualWidth(ch);
  }
  return width;
};

/** Slice by visual budget; returns { text, width } of the kept prefix. */
const sliceByVisualWidth = (
  text: string,
  maxWidth: number,
): { text: string; width: number } => {
  if (maxWidth <= 0 || !text) {
    return { text: '', width: 0 };
  }
  let width = 0;
  let end = 0;
  for (const ch of text) {
    const charWidth = getCharVisualWidth(ch);
    if (width + charWidth > maxWidth) {
      break;
    }
    width += charWidth;
    end += ch.length;
  }
  return { text: text.slice(0, end), width };
};

const truncateByVisualWidth = (text: string, maxWidth: number): string => {
  if (getVisualWidth(text) <= maxWidth) {
    return text;
  }
  // Reserve ~3 Latin units for "...".
  const ellipsisWidth = 3;
  const keep = sliceByVisualWidth(text, Math.max(maxWidth - ellipsisWidth, 1)).text;
  return `${keep}...`;
};

/**
 * Last space whose visual offset is still within `maxWidth` (for English wrap).
 * Returns character index, or -1.
 */
const findLastSpaceWithinVisualWidth = (
  text: string,
  maxWidth: number,
): number => {
  let width = 0;
  let lastSpaceIdx = -1;
  let index = 0;
  for (const ch of text) {
    if (width + getCharVisualWidth(ch) > maxWidth) {
      break;
    }
    width += getCharVisualWidth(ch);
    if (ch === ' ') {
      lastSpaceIdx = index;
    }
    index += ch.length;
  }
  return lastSpaceIdx;
};

export const wrapChartAxisLabel = (
  label: string,
  charsPerLine = 10,
  maxLines = 2,
): string => {
  if (!label) {
    return '';
  }

  const maxWidth = charsPerLine * maxLines;
  // Cap total visual length first so 2 wrapped lines cannot grow unbounded.
  const truncated = truncateByVisualWidth(label, maxWidth);

  if (getVisualWidth(truncated) <= charsPerLine) {
    return truncated;
  }

  // Prefer wrapping after a space near the first-line budget (English names).
  const spaceIdx = findLastSpaceWithinVisualWidth(truncated, charsPerLine);
  if (spaceIdx > 0 && spaceIdx < truncated.length - 1) {
    const line1 = truncated.slice(0, spaceIdx);
    const line2 = truncateByVisualWidth(
      truncated.slice(spaceIdx + 1),
      charsPerLine,
    );
    return `${line1}\n${line2}`;
  }

  // No usable space (CJK / single long token): hard wrap by visual width.
  const line1 = sliceByVisualWidth(truncated, charsPerLine).text;
  const line2 = truncateByVisualWidth(
    truncated.slice(line1.length),
    charsPerLine,
  );
  return `${line1}\n${line2}`;
};

/**
 * Same visual-width wrap as ticks, but keep the full string (no "...").
 * Used for hover axisPointer so long CJK names break into lines instead of one wide overlay.
 */
export const wrapChartAxisLabelFull = (
  label: string,
  charsPerLine = 10,
): string => {
  if (!label) {
    return '';
  }
  if (getVisualWidth(label) <= charsPerLine) {
    return label;
  }

  const lines: string[] = [];
  let rest = label;
  while (rest) {
    if (getVisualWidth(rest) <= charsPerLine) {
      lines.push(rest);
      break;
    }

    const spaceIdx = findLastSpaceWithinVisualWidth(rest, charsPerLine);
    if (spaceIdx > 0) {
      lines.push(rest.slice(0, spaceIdx));
      rest = rest.slice(spaceIdx + 1);
      continue;
    }

    const { text: line } = sliceByVisualWidth(rest, charsPerLine);
    // Single glyph wider than budget should not loop forever.
    if (!line) {
      lines.push(rest);
      break;
    }
    lines.push(line);
    rest = rest.slice(line.length);
  }

  return lines.join('\n');
};

export const applyWrappedXAxisLabels = (
  opt: any,
  charsPerLine = 10,
  maxLines = 2,
): void => {
  const primaryXAxis = Array.isArray(opt?.xAxis)
    ? opt.xAxis?.[0]
    : opt?.xAxis;
  if (!primaryXAxis) {
    return;
  }

  // Ticks: wrap + truncate. Hover: same wrap rules, full name (no ellipsis).
  primaryXAxis.axisLabel = {
    ...(primaryXAxis.axisLabel || {}),
    margin: 8,
    interval: 0,
    // ECharts renders `\n` as a line break in axis labels.
    formatter: (label: string) => wrapChartAxisLabel(label, charsPerLine, maxLines),
  };

  if (!opt.tooltip) {
    opt.tooltip = {};
  }
  if (!opt.tooltip.axisPointer) {
    opt.tooltip.axisPointer = {};
  }
  opt.tooltip.axisPointer.label = {
    ...(opt.tooltip.axisPointer.label || {}),
    show: true,
    formatter: (params: { value?: any }) => (
      wrapChartAxisLabelFull(String(params?.value ?? ''), charsPerLine)
    ),
  };
};

/** @deprecated Use applyWrappedXAxisLabels */
export const applyTruncatedXAxisLabels = applyWrappedXAxisLabels;
